import classNames from 'classnames';
import { Atom, atom, useAtom, useAtomValue } from 'jotai';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMapbox, {
  MapLayerMouseEvent,
  MapProps,
  MapRef,
  NavigationControl,
  FullscreenControl,
} from 'react-map-gl';

import type { TitleWindow } from 'types/window';
import { TierLayer } from './tier-layer';
import { DevelopmentAreasLayer } from './development-areas-layer';
import LandGrid from './land-grid';
import MapPopup from './map-popup';
import { standardMapStyles } from './map-styles';

import style from './map.module.scss';
import { StylesSelector } from './style-selector';
import {
  isLocationHovered,
  normalizeMapConfig,
  setPopupState,
  usePopup,
} from './utils';
import { WellsV2Layer } from './wells-v2-layer';
import { BasinLayer } from './basin-layer';
import { MapUserConfigAtom } from './types';
import { mapClicksTakenAtom, mapOverlaysAtom, measureModeAtom } from './atoms';
import { appStore } from './overlay-slot';
import { MeasureControl } from './measure-control';
import { TypeCurvesLayer } from './type-curves-layer';
import { TitleEntityAssetsLayer } from './title-entity-assets-layer';
import { StructureGridLayer } from '../structure-grids/structure-grid-layer';
import { addPatternImages } from './add-pattern-images';
import { FocusAreaLayer } from './focus-area-layer';
import { PipelineLayer } from './pipeline-layer';
import { PortfolioAssetsLayer } from './portfolio-assets-layer';
import { DrillingRigLayer } from './drilling-rigs/drilling-rig-layer';
import { addDrillingRigImages } from './drilling-rigs/add-drilling-rig-images';
import { addPipelineImages } from './pipelines/add-pipeline-images';
import { useEnforceLayerOrder } from './use-enforce-layer-order';
declare let window: TitleWindow;

interface Props extends Omit<MapProps, 'fog' | 'terrain'> {
  longitude: number;
  latitude: number;
  zoom?: number;
  showControls?: boolean;
  showMapFilters?: boolean;
  geographies?: {
    id: number;
    name: string;
    quarterCalls?: string[];
  }[];
  blockPopups?: boolean;
  className?: string;
  mapConfigAtom: MapUserConfigAtom;
  scopedLayers?: ReactNode;
  showPopupOnSingleDevArea?: boolean;
  disableWellsLayer?: boolean;
  /** Suppress the General "Type Curves" layer without touching `mapConfig`. */
  typeCurvesVisible?: boolean;
  isLandingPageMap?: boolean;
  refAtom?: Atom<MapRef | null>;
  onResetZoom?: () => void;
  /** Overrides the Portfolio Assets tract-polygon color (fill + outline). When
   *  omitted the layer keeps its default yellow. */
  portfolioAssetsColor?: string;
  /** Structure grid collection the page is scoped to, when it has one. Lets the
   *  well popup read the landing-analysis Landing Zone for that collection
   *  instead of the stored SRP value. Landing is stored per collection, so a
   *  map with no collection in scope leaves this undefined and the popup keeps
   *  showing the vendor value. */
  structureGridCollectionId?: number | null;
  /** Overrides Portfolio Assets visibility. When omitted the layer falls back
   *  to `mapConfig.portfolioAssets.checked`. */
  portfolioAssetsVisible?: boolean;
  /** Opt into native map-gl zoom gestures that are disabled by default when
   *  controls are shown (pinch/two-finger zoom and double-click zoom). */
  enableTouchZoom?: boolean;
  enableDoubleClickZoom?: boolean;
  /**
   * Hide the pricing ("$") and measure-distance controls from the map's control
   * stack. Defaults to `false` (both shown). Used by focused embeds — e.g. the
   * "Manage type curves" modal map — that don't need those page-map tools and
   * where they'd otherwise overlap the layers panel.
   */
  hideMeasureAndPricing?: boolean;
}
export const mapStylesIndexAtom = atom(0);

export const mapRefAtom = atom<MapRef | null>(null);

const Map = ({
  longitude,
  latitude,
  zoom = 12,
  children,
  showControls,
  showMapFilters = false,
  className,
  geographies = [],
  blockPopups = false,
  mapConfigAtom,
  scopedLayers,
  showPopupOnSingleDevArea,
  disableWellsLayer = false,
  typeCurvesVisible = true,
  isLandingPageMap = false,
  refAtom,
  onResetZoom,
  portfolioAssetsColor,
  structureGridCollectionId,
  portfolioAssetsVisible,
  enableTouchZoom = false,
  enableDoubleClickZoom = false,
  hideMeasureAndPricing = false,
  ...rest
}: Props) => {
  mapConfigAtom.onMount = (set) => set(normalizeMapConfig);
  const mapStyleIndex = useAtomValue(mapStylesIndexAtom);
  const [mapRef, setMapRef] = useAtom(refAtom || mapRefAtom);
  const wellsV2MinZoom = useMemo(() => {
    void mapStyleIndex; // recalculate when map style changes (sources are recreated)
    // @ts-expect-error AnySourceImpl is a general type defined by mapbox and doesn't have minzoom defined
    return mapRef?.getMap().getSource('wells-v2')?.minzoom;
  }, [mapRef, mapStyleIndex]);
  const [currentZoom, setCurrentZoom] = useState<number | undefined>(() =>
    mapRef?.getMap().getZoom()
  );
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;
    setCurrentZoom(map.getZoom());
    const onZoomEnd = () => setCurrentZoom(map.getZoom());
    map.on('zoomend', onZoomEnd);
    return () => {
      map.off('zoomend', onZoomEnd);
    };
  }, [mapRef, mapStyleIndex]);

  const mapConfig = useAtomValue(mapConfigAtom);
  const measureMode = useAtomValue(measureModeAtom);
  const clicksTaken = useAtomValue(mapClicksTakenAtom);
  const overlays = useAtomValue(mapOverlaysAtom, appStore);

  // Enforce a fixed layer z-order regardless of toggle/mount timing
  useEnforceLayerOrder(mapRef);

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    const addAll = () => {
      addPatternImages(map);
      addPipelineImages(map);
      addDrillingRigImages(map);
    };

    if (map.isStyleLoaded()) addAll();
    map.on('style.load', addAll);
    return () => {
      map.off('style.load', addAll);
    };
  }, [mapRef]);

  useEffect(() => {
    mapRef?.jumpTo({ center: [longitude, latitude], zoom });
  }, [longitude, latitude, zoom, mapRef]);

  const [popup, setPopup] = usePopup();

  const popupRef = useRef(popup);
  popupRef.current = popup;

  const onMapMove = useCallback(
    (e: MapLayerMouseEvent) => {
      if (
        !popupRef.current.pinned &&
        mapConfig.popups.checked &&
        !blockPopups &&
        !clicksTaken
      ) {
        setPopupState({ e, setPopup });
      }
    },
    [setPopup, mapConfig.popups.checked, blockPopups, clicksTaken]
  );

  const onClosePopUp = useCallback(() => {
    setPopup({ latitude: 0, longitude: 0, visible: false });
  }, [setPopup]);

  const onDragStart = useCallback(() => {
    setPopup({ latitude: 0, longitude: 0, visible: false });
  }, [setPopup]);

  const onDblClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!mapRef || clicksTaken) return;

      // A fresh hover popup over a feature: pin it, and on double-click-zoom
      // maps cancel the zoom so it doesn't jump while selecting. An
      // already-pinned popup (or empty map) keeps native double-click zoom.
      if (popupRef.current.visible && !popupRef.current.pinned) {
        if (enableDoubleClickZoom) e.preventDefault();
        setPopup((current) => ({
          ...current,
          pinned: true,
        }));
      }
    },
    [setPopup, mapRef, clicksTaken, enableDoubleClickZoom]
  );

  const hoveredReferenceLocations = geographies.flatMap(
    ({ id, name, quarterCalls }) => {
      if (isLocationHovered(popup, id)) {
        return [
          {
            id,
            name,
            quarterCalls,
          },
        ];
      }
      return [];
    }
  );

  const styles = standardMapStyles[mapStyleIndex];
  const settings = showControls
    ? {
        dragRotate: false,
        scrollZoom: mapConfig.scrollZoom.checked,
        touchZoom: enableTouchZoom,
        touchRotate: false,
        keyboard: false,
        doubleClickZoom: enableDoubleClickZoom,
        showCompass: false,
        fadeDuration: 0,
      }
    : undefined;

  className = classNames(style.container, className);
  return (
    <div className={className}>
      <div className={style.mapArea}>
        <ReactMapbox
          ref={setMapRef}
          mapboxAccessToken={window.CONFIG.mapbox.token}
          testMode={window.CONFIG.mapbox.testMode}
          mapStyle={styles.url}
          onMouseMove={onMapMove}
          onDblClick={onDblClick}
          onDragStart={onDragStart}
          initialViewState={{ latitude, longitude, zoom }}
          style={{ width: '100%', height: '100%' }}
          {...rest}
          {...settings}
        >
          <LandGrid />
          <StructureGridLayer mapConfigAtom={mapConfigAtom} />
          <TypeCurvesLayer
            mapConfigAtom={mapConfigAtom}
            visible={typeCurvesVisible}
          />
          <BasinLayer mapConfigAtom={mapConfigAtom} />
          <TierLayer mapConfigAtom={mapConfigAtom} />
          <TitleEntityAssetsLayer
            mapConfigAtom={mapConfigAtom}
            mapRef={mapRef}
          />
          <PortfolioAssetsLayer
            mapConfigAtom={mapConfigAtom}
            fillColor={portfolioAssetsColor}
            lineColor={portfolioAssetsColor}
            visible={portfolioAssetsVisible}
          />
          <DevelopmentAreasLayer mapConfigAtom={mapConfigAtom} />
          {mapConfig.wellsV2?.checked && !disableWellsLayer && (
            <WellsV2Layer mapConfigAtom={mapConfigAtom} />
          )}
          <PipelineLayer mapConfigAtom={mapConfigAtom} />
          <DrillingRigLayer mapConfigAtom={mapConfigAtom} />
          <MapPopup
            onClose={onClosePopUp}
            locations={hoveredReferenceLocations}
            popup={popup}
            showPopupOnSingleDevArea={showPopupOnSingleDevArea}
            structureGridCollectionId={structureGridCollectionId}
          />
          <FocusAreaLayer mapConfigAtom={mapConfigAtom} />
          {measureMode && <MeasureControl />}
          {overlays.map(({ id, component: Overlay }) => (
            <Overlay key={id} />
          ))}
          {children}
          {showControls ? <NavigationControl showCompass={false} /> : null}
          <FullscreenControl />
          <StylesSelector
            showMapFilters={showMapFilters}
            mapConfigAtom={mapConfigAtom}
            scopedLayers={scopedLayers}
            wellsV2MinZoom={wellsV2MinZoom}
            currentZoom={currentZoom}
            isLandingPageMap={isLandingPageMap}
            onResetZoom={onResetZoom}
            hideMeasureAndPricing={hideMeasureAndPricing}
          />
        </ReactMapbox>
      </div>
    </div>
  );
};

const MapProvider: FC<Props> = (props) => {
  return <Map {...props} />;
};

export default MapProvider;
